// src/components/DegreeAudit/Overrides/RequirementOverrideControl.tsx
// W25 lean override slice — the inline override affordance for a single audit
// requirement. Presentational: all data + actions are injected, so it's trivially
// testable and decoupled from how the audit tree supplies overrides.
//
// Honesty (W17 §5.3): when a requirement is satisfied via an override, this shows
// an explicit "Overridden" pill — the requirement's RAW status is still rendered
// by the parent audit row, so both are visible. The override never silently hides
// the underlying state.
import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';

import OverrideRequestModal from '../../shared/modals/OverrideRequestModal';
import { Override, OverrideDraft } from '../../../services/overrideService';

interface RequirementOverrideControlProps {
  requirementCode: string;
  requirementName?: string;
  /** Raw status: is the requirement naturally satisfied (no override needed)? */
  isSatisfied: boolean;
  /** The active override for this requirement, if any. */
  activeOverride?: Override | null;
  onCreate: (draft: OverrideDraft) => Promise<void>;
  onRevoke: (overrideId: number, reason: string) => Promise<void>;
}

const RequirementOverrideControl = ({
  requirementCode,
  requirementName,
  isSatisfied,
  activeOverride,
  onCreate,
  onRevoke,
}: RequirementOverrideControlProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [busy, setBusy] = useState(false);

  if (activeOverride) {
    const handleRevoke = async () => {
      setBusy(true);
      try {
        await onRevoke(activeOverride.id, 'Removed by student');
      } finally {
        setBusy(false);
        setConfirmingRevoke(false);
      }
    };

    return (
      <div className="flex items-center gap-2 text-xs">
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent bg-opacity-10 text-accent font-medium"
          title="Satisfied via a student-recorded override; the raw status is still shown."
        >
          <ShieldCheck className="h-3 w-3" />
          Overridden
        </span>
        {confirmingRevoke ? (
          <>
            <span className="text-gray-500">Remove?</span>
            <button
              type="button"
              onClick={handleRevoke}
              disabled={busy}
              className="text-red-600 hover:underline disabled:opacity-50"
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => setConfirmingRevoke(false)}
              disabled={busy}
              className="text-gray-500 hover:underline"
            >
              No
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingRevoke(true)}
            className="text-gray-400 hover:text-gray-700 hover:underline"
          >
            Remove override
          </button>
        )}
      </div>
    );
  }

  // No override. Offer one only for an unsatisfied requirement.
  if (isSatisfied) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-accent hover:underline"
      >
        <ShieldCheck className="h-3 w-3" />
        Override
      </button>
      <OverrideRequestModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        constraintId={requirementCode}
        requirementName={requirementName}
        onSubmit={onCreate}
      />
    </>
  );
};

export default RequirementOverrideControl;
